import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { LimitForm } from './limit-form';
import {
	buildCreateLimitInput,
	generateIdempotentLimitId,
	type LimitFormSubmitData
} from './limit-input';
import { LIMIT_CREATE_MUTATION } from './limits.gql.tsx';

interface CloneData {
	limitId?: string;
	name?: string;
	minAmount?: number | null;
	maxAmount?: number | null;
	minStayMinutes?: number | null;
	maxStayMinutes?: number | null;
}

interface LocationState {
	cloneData?: CloneData;
}

export const LimitsCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;

	// Use the pre-generated limitId from clone data, or generate a new one
	const idempotentLimitId = useMemo(
		() => cloneData?.limitId ?? generateIdempotentLimitId(),
		[cloneData?.limitId]
	);

	const [createLimit, { loading: isSubmitting }] = useMutation(
		LIMIT_CREATE_MUTATION
	);

	// Set up page header
	useEffect(() => {
		setPageHeader({
			title: cloneData ? 'Clone Limit' : 'Create Limit',
			description: cloneData ? 'Create a new limit based on an existing one' : 'Create a new limit for your system',
			showBackButton: true,
			onBackClick: () => go({ to: '/limits' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go, cloneData]);

	const handleSubmit = async (formData: LimitFormSubmitData) => {
		const payload = buildCreateLimitInput(formData, idempotentLimitId);

		try {
			const { data } = await createLimit(
				getMutationOptions('limits', { input: payload })
			);

			const message = extractCheckResultError(data?.createLimit);

			if (message) {
				showMutationErrorToast('limits', 'create', { message });
				return;
			}

			showMutationSuccessToast('limits', 'create');
			// Navigate back with the newly created limit ID for selection
			go({
				to: '/limits',
				state: { selectLimitId: idempotentLimitId }
			});
		} catch (error) {
			showMutationErrorToast('limits', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			<LimitForm
				mode={cloneData ? 'clone' : 'create'}
				initialData={cloneData ? {
					name: cloneData.name ?? '',
					minAmount: cloneData.minAmount ?? undefined,
					maxAmount: cloneData.maxAmount ?? undefined,
					minStayMinutes: cloneData.minStayMinutes ?? undefined,
					maxStayMinutes: cloneData.maxStayMinutes ?? undefined
				} : undefined}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle={cloneData ? 'Clone Limit' : 'Create Limit'}
			/>
		</FormPageLayout>
	);
};
